import { computed } from "@livestore/livestore";
import { useStore } from "@livestore/react";
import { dev } from "@phosphor/devstring";
import { DisposePool } from "@phosphor/utils";
import { type KeyboardEvent, useEffect, useMemo, useRef } from "react";
import { emitDebugValueFn } from "#scripts/lib/dev/emitDebugValue.ts";
import { cn } from "#scripts/lib/utils.ts";
import { useStateMachine } from "../hooks/useStateMachine.ts";
import type { AutocompleteVM } from "../types.ts";
import { Queryable } from "./Queryable.tsx";

interface AutocompleteInputProps {
  vm: AutocompleteVM;
}

export function AutocompleteInput({ vm }: AutocompleteInputProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  // #hide debugs
  const { store } = useStore();
  const optionList$ = useMemo(() => {
    return computed(
      (query) =>
        (query(vm.options$ as never) as any).map((a: any) => (query(a.selected$) ? "✓ " : "  ") + a.label).join("\n"),
      { label: "optionList" },
    );
  }, [vm.options$]);
  useEffect(() => {
    const pool = new DisposePool();
    const emitOptions = emitDebugValueFn("!autocomplete-options", dev``);
    store.subscribe(optionList$, (optionList) => emitOptions(optionList, true));
    store.subscribe(vm.inputText$, emitDebugValueFn("!autocomplete-inputText", dev``));
    return () => {
      pool.dispose();
    };
  }, [store, optionList$, vm.inputText$]);

  const __interrupt__ = useStateMachine(
    "Update @ind↓↓\nwith @help\n...",
    {
      "↓": () => (vm.pressKey("down"), 1000),
      "\n": () => (vm.pressKey("enter"), 300),
      ".": () => 1000,
      orDefault: (letter) => (vm.updateInput(store.query(vm.inputText$) + letter, "end"), Math.random() * 200 + 100),
      reset: () => (vm.updateInput("", "end"), 1000),
    },
    { observeRef: containerRef },
  );

  // #endhide debugs
  return (
    <div ref={containerRef} className="relative w-full">
      <Queryable query={vm.inputText$}>
        {(inputText: string) => {
          const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
            __interrupt__(); // #hide
            switch (e.key) {
              case "ArrowDown":
                e.preventDefault();
                vm.pressKey("down");
                break;
              case "ArrowUp":
                e.preventDefault();
                vm.pressKey("up");
                break;
              case "Enter":
                e.preventDefault();
                vm.pressKey("enter");
                break;
              case "Escape":
                e.preventDefault();
                vm.pressKey("escape");
                break;
            }
          };

          return (
            <>
              <textarea
                value={inputText}
                onChange={(e) => {
                  const target = e.currentTarget;
                  vm.updateInput(target.value, target.selectionStart);
                }}
                onKeyDown={handleKeyDown}
                onFocus={() => __interrupt__()} // #hide
                placeholder="Type @ followed by a filename..."
                className="w-full px-3 py-2 text-sm border border-border rounded-md bg-bg-elevated text-text resize-none focus:outline-hidden focus:ring-2 focus:ring-primary/50"
                rows={3}
              />
              <Queryable query={vm.options$}>
                {(options) =>
                  options.length > 0 && (
                    <div
                      className="absolute z-10 w-full mt-1 border rounded-md shadow-lg max-h-60 overflow-auto"
                      style={{
                        background: "oklch(0.12 0.03 var(--hue-neutral))",
                        borderColor: "oklch(0.25 0.03 var(--hue-neutral))",
                      }}
                    >
                      {options.map((option) => (
                        <Queryable key={option.key} query={option.selected$}>
                          {(isSelected: boolean) => {
                            const buttonRef = useRef<HTMLButtonElement>(null);

                            useEffect(() => {
                              if (isSelected && buttonRef.current) {
                                if (__interrupt__.isActive()) return; // #hide
                                buttonRef.current.scrollIntoView({
                                  block: "nearest",
                                  behavior: "smooth",
                                });
                              }
                            }, [isSelected]);

                            return (
                              <button
                                ref={buttonRef}
                                type="button"
                                onClick={() => option.click()}
                                className={cn(
                                  "w-full px-3 py-2 text-left text-sm border-l-2 border-transparent",
                                  !isSelected && "hover:bg-accent-muted/10",
                                  isSelected && "border-l-accent",
                                )}
                                style={
                                  isSelected
                                    ? {
                                        background: "oklch(0.9 0.2 var(--hue-accent) / 0.15)",
                                        color: "var(--color-accent)",
                                      }
                                    : undefined
                                }
                              >
                                <span className="font-mono text-xs">{option.label}</span>
                              </button>
                            );
                          }}
                        </Queryable>
                      ))}
                    </div>
                  )
                }
              </Queryable>
            </>
          );
        }}
      </Queryable>
    </div>
  );
}
